import { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { Plus, Trash2, Loader2, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { euro, toInputDate } from "@/lib/format";
import api from "@/lib/api";
import PreziarioPicker from "@/components/PreziarioPicker";

export const CATEGORIE = [
  "Demolizioni e Rimozioni", "Opere Edili e Isolamenti", "Serramenti e Infissi", "Impianti (Assistenze Edili)",
  "Finiture (Posature e Pitturazioni)", "Sicurezza di Cantiere", "001 - Fornitura Materiali", "002 - Manodopera", "Generale",
];
const UNITA = ["mq", "ml", "cad", "a corpo", "mc", "kg", "ore", "gg"];
const TERMINI = "40% anticipato · 30% avanzamento lavori · 30% consegna lavori";

const AutoTextarea = ({ value, className, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [value]);
  return <Textarea ref={ref} value={value} rows={1} className={className} {...props} />;
};

const emptyLine = () => ({ codice: "", categoria: "Generale", nome: "", unita: "cad", quantita: 1, prezzo: 0, iva: 22, sconto: 0 });
const lineBase = (v) => Number(v.quantita) * Number(v.prezzo) * (1 - Number(v.sconto || 0) / 100);

// Totali con lo sconto generale ripartito su tutte le voci (come fa il server).
export const quoteTotals = (voci, tipo, valore) => {
  const lordo = voci.reduce((s, v) => s + lineBase(v), 0);
  const iva = voci.reduce((s, v) => s + lineBase(v) * (Number(v.iva) / 100), 0);
  const val = Math.max(0, Number(valore) || 0);
  const sconto = tipo === "importo" ? Math.min(val, lordo) : (lordo * Math.min(val, 100)) / 100;
  const f = lordo ? (lordo - sconto) / lordo : 1;
  return { lordo, sconto, imponibile: lordo - sconto, iva: iva * f, totale: (lordo - sconto) + iva * f };
};

const More = ({ open, onToggle, label, testid }) => (
  <button type="button" onClick={onToggle} className="inline-flex items-center gap-1.5 min-h-[44px] px-2 text-sm font-medium text-primary" data-testid={testid}>
    <SlidersHorizontal className="w-4 h-4" /> {label} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
  </button>
);

const LineItem = ({ v, i, prices, preziario, onChange, onRemove }) => {
  const [more, setMore] = useState(false);
  const fromPrice = (pid) => {
    const p = prices.find((x) => x.id === pid);
    if (p) onChange({ nome: p.nome, prezzo: p.prezzo, iva: p.iva, codice: p.codice, categoria: p.categoria, unita: p.unita });
  };
  return (
    <div className="rounded-xl border border-border p-3 sm:p-4 space-y-3 bg-secondary/30" data-testid={`quote-line-${i}`}>
      <div className="flex gap-2 items-start">
        <div className="flex-1 space-y-1.5">
          <Label className="text-sm">Descrizione del lavoro</Label>
          <AutoTextarea placeholder="Es. Rifacimento bagno: demolizione, impianti, piastrelle" value={v.nome} onChange={(e) => onChange({ nome: e.target.value })}
            className="text-base leading-snug min-h-[48px] resize-y" list="designazione-list" data-testid={`quote-line-name-${i}`} />
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-11 w-11 mt-6 text-muted-foreground hover:text-destructive shrink-0" onClick={onRemove} aria-label="Elimina voce" data-testid={`quote-line-remove-${i}`}>
          <Trash2 className="w-5 h-5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-sm">Quantità{v.unita ? ` (${v.unita})` : ""}</Label>
          <Input type="number" inputMode="decimal" min="0" value={v.quantita} onChange={(e) => onChange({ quantita: e.target.value })} className="h-11 text-base" data-testid={`quote-line-qty-${i}`} /></div>
        <div className="space-y-1.5"><Label className="text-sm">Prezzo €</Label>
          <Input type="number" inputMode="decimal" min="0" step="0.01" value={v.prezzo} onChange={(e) => onChange({ prezzo: e.target.value })} className="h-11 text-base" data-testid={`quote-line-price-${i}`} /></div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <More open={more} onToggle={() => setMore((m) => !m)} label="Altro" testid={`quote-line-more-${i}`} />
        <span className="text-sm text-muted-foreground">Totale voce: <b className="text-foreground">{euro(lineBase(v))}</b></span>
      </div>
      {more && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1" data-testid={`quote-line-extra-${i}`}>
          <div className="space-y-1.5"><Label className="text-sm">Unità di misura</Label>
            <Select value={v.unita} onValueChange={(u) => onChange({ unita: u })}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITA.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-sm">Sconto su questa voce %</Label><Input type="number" min="0" max="100" value={v.sconto} onChange={(e) => onChange({ sconto: e.target.value })} className="h-11" /></div>
          <div className="space-y-1.5"><Label className="text-sm">IVA %</Label><Input type="number" min="0" value={v.iva} onChange={(e) => onChange({ iva: e.target.value })} className="h-11" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Codice articolo</Label><Input value={v.codice} onChange={(e) => onChange({ codice: e.target.value })} className="h-11" /></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-sm">Categoria</Label><Input list="cat-list" value={v.categoria} onChange={(e) => onChange({ categoria: e.target.value })} className="h-11" /></div>
          {prices.length > 0 && (
            <div className="space-y-1.5 col-span-2 sm:col-span-3"><Label className="text-sm">Prendi dal mio listino prezzi</Label>
              <Select onValueChange={fromPrice}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Scegli una voce del tuo listino" /></SelectTrigger>
                <SelectContent>{prices.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          {preziario.length > 0 && (
            <div className="space-y-1.5 col-span-2 sm:col-span-3"><Label className="text-sm">Prendi dal prezzario regionale</Label>
              <PreziarioPicker items={preziario} onPick={(p) => onChange({ nome: p.nome, prezzo: p.prezzo_finale, codice: p.codice, categoria: p.categoria || p.preziario_nome || "Prezzario", unita: p.unita || "cad" })} /></div>
          )}
        </div>
      )}
    </div>
  );
};

const Discount = ({ tipo, valore, setTipo, setValore }) => (
  <div className="rounded-xl border border-border p-3 sm:p-4 space-y-2" data-testid="quote-discount">
    <Label className="text-sm">Sconto sul totale</Label>
    <div className="flex gap-2">
      <div className="grid grid-cols-2 rounded-lg bg-secondary p-1 shrink-0" role="radiogroup">
        {[["percentuale", "%"], ["importo", "€"]].map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={tipo === k} onClick={() => setTipo(k)} data-testid={`quote-discount-type-${k}`}
            className={`min-w-[44px] h-10 rounded-md text-base font-semibold ${tipo === k ? "bg-background shadow-sm" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <Input type="number" inputMode="decimal" min="0" value={valore} onChange={(e) => setValore(e.target.value)} className="h-12 text-base" placeholder="0" data-testid="quote-discount-value" />
    </div>
  </div>
);

export default function QuoteBuilder({ open, onClose, onSave, contacts, prices, preziario = [], initial, mode = "edit" }) {
  const [contactId, setContactId] = useState("");
  const [clienteNome, setClienteNome] = useState("");
  const [numero, setNumero] = useState("");
  const [oggetto, setOggetto] = useState("");
  const [dataEmissione, setDataEmissione] = useState("");
  const [scadenza, setScadenza] = useState("");
  const [stato, setStato] = useState("bozza");
  const [voci, setVoci] = useState([emptyLine()]);
  const [note, setNote] = useState("");
  const [garanzia, setGaranzia] = useState("");
  const [termini, setTermini] = useState(TERMINI);
  const [scTipo, setScTipo] = useState("percentuale");
  const [scValore, setScValore] = useState(0);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const in15 = new Date(today.getTime() + 15 * 86400000);
    const q = initial || {};
    const edit = !!initial && mode === "edit";
    setContactId(q.contact_id || "");
    setClienteNome(q.cliente_nome || "");
    setOggetto(q.oggetto || "");
    setDataEmissione(edit ? (toInputDate(q.data_emissione) || toInputDate(today.toISOString())) : toInputDate(today.toISOString()));
    setScadenza(edit ? toInputDate(q.scadenza) : toInputDate(in15.toISOString()));
    setStato(edit ? (q.stato || "bozza") : "bozza");
    setVoci(q.voci?.length ? q.voci.map((v) => ({ ...emptyLine(), ...v })) : [emptyLine()]);
    setNote(q.note || "");
    setGaranzia(q.garanzia || "");
    setTermini(q.termini_pagamento || TERMINI);
    setScTipo(q.sconto_tipo || "percentuale");
    setScValore(q.sconto_valore || 0);
    setMore(false);
    if (edit) setNumero(q.numero || "");
    else api.get("/quotes/next-number").then((r) => setNumero(String(r.data.numero))).catch(() => setNumero(""));
  }, [open, initial, mode]);

  const updateLine = (i, patch) => setVoci((v) => v.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const tot = quoteTotals(voci, scTipo, scValore);

  const handleContact = (id) => {
    setContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (c) setClienteNome(c.ragione_sociale);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        contact_id: contactId || null, cliente_nome: clienteNome, oggetto,
        data_emissione: dataEmissione ? new Date(dataEmissione).toISOString() : "",
        scadenza: scadenza ? new Date(scadenza).toISOString() : "", stato,
        voci: voci.filter((v) => v.nome).map((v) => ({
          codice: v.codice || "", categoria: v.categoria || "Generale", nome: v.nome, unita: v.unita || "cad",
          quantita: Number(v.quantita) || 0, prezzo: Number(v.prezzo) || 0, iva: Number(v.iva) || 0, sconto: Number(v.sconto) || 0,
        })),
        note, garanzia, termini_pagamento: termini, numero: numero || null,
        sconto_tipo: scTipo, sconto_valore: Number(scValore) || 0,
      });
    } finally {
      setSaving(false);
    }
  };

  const designazioni = useMemo(() => {
    const set = new Set();
    (prices || []).forEach((p) => p.nome && set.add(p.nome));
    (preziario || []).slice(0, 500).forEach((p) => p.nome && set.add(p.nome));
    return Array.from(set);
  }, [prices, preziario]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92dvh] overflow-y-auto p-4 sm:p-6" data-testid="quote-builder-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{mode === "edit" ? `Modifica preventivo N. ${initial?.numero || ""}` : "Nuovo preventivo"}</DialogTitle>
          <DialogDescription>Cliente, lavori e prezzi: il resto lo trovi in «Altro».</DialogDescription>
        </DialogHeader>
        <datalist id="cat-list">{CATEGORIE.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="designazione-list">{designazioni.map((n) => <option key={n} value={n} />)}</datalist>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Cliente *</Label>
            <Input value={clienteNome} onChange={(e) => { setClienteNome(e.target.value); setContactId(""); }} placeholder="Scrivi il nome del cliente" className="h-12 text-base" data-testid="quote-client-name-input" />
            {contacts.length > 0 && (
              <Select value={contactId} onValueChange={handleContact}>
                <SelectTrigger className="h-11" data-testid="quote-client-select"><SelectValue placeholder="…oppure sceglilo dalla rubrica" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-3">
            {voci.map((v, i) => (
              <LineItem key={i} v={v} i={i} prices={prices || []} preziario={preziario || []} onChange={(p) => updateLine(i, p)}
                onRemove={() => setVoci((vs) => (vs.length > 1 ? vs.filter((_, idx) => idx !== i) : [emptyLine()]))} />
            ))}
            <Button type="button" variant="outline" className="w-full h-12 text-base" onClick={() => setVoci((v) => [...v, emptyLine()])} data-testid="quote-add-line-item-button">
              <Plus className="w-5 h-5 mr-1.5" /> Aggiungi un altro lavoro
            </Button>
          </div>

          <Discount tipo={scTipo} valore={scValore} setTipo={setScTipo} setValore={setScValore} />

          <div className="rounded-xl bg-secondary p-4 text-base space-y-1.5" data-testid="quote-builder-totals">
            {tot.sconto > 0 && <>
              <div className="flex justify-between"><span className="text-muted-foreground">Totale lavori</span><span>{euro(tot.lordo)}</span></div>
              <div className="flex justify-between text-emerald-700 dark:text-emerald-400"><span>Sconto</span><span data-testid="quote-builder-discount">− {euro(tot.sconto)}</span></div>
            </>}
            <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="font-medium">{euro(tot.imponibile)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="font-medium">{euro(tot.iva)}</span></div>
            <div className="flex justify-between text-lg font-bold text-primary pt-1.5 border-t border-border"><span>Totale</span><span data-testid="quote-builder-total">{euro(tot.totale)}</span></div>
          </div>

          <More open={more} onToggle={() => setMore((m) => !m)} label="Altro: numero, date, stato, pagamento, note" testid="quote-more-toggle" />
          {more && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="quote-more-section">
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm">Oggetto del preventivo</Label><Input value={oggetto} onChange={(e) => setOggetto(e.target.value)} placeholder="Es. Ristrutturazione bagno — Via Roma 10" className="h-11" data-testid="quote-object-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Numero preventivo</Label><Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Automatico" className="h-11" data-testid="quote-number-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Stato</Label>
                <Select value={stato} onValueChange={setStato}>
                  <SelectTrigger className="h-11" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem><SelectItem value="inviato">Inviato</SelectItem>
                    <SelectItem value="accettato">Accettato</SelectItem><SelectItem value="rifiutato">Rifiutato</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-sm">Data</Label><DateInput value={dataEmissione} onChange={(e) => setDataEmissione(e.target.value)} data-testid="quote-date-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Valido fino al</Label><DateInput value={scadenza} onChange={(e) => setScadenza(e.target.value)} data-testid="quote-deadline-input" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm">Come ti pagherà il cliente</Label><Input value={termini} onChange={(e) => setTermini(e.target.value)} className="h-11" data-testid="quote-terms-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Nota</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Vuoto = nota predefinita" data-testid="quote-note-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Garanzia</Label><Textarea value={garanzia} onChange={(e) => setGaranzia(e.target.value)} rows={3} placeholder="Vuoto = garanzia predefinita" data-testid="quote-garanzia-input" /></div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 sticky bottom-0 bg-background pt-3">
          <Button variant="outline" className="h-12" onClick={onClose}>Annulla</Button>
          <Button className="h-12 text-base" onClick={save} disabled={saving || !clienteNome.trim()} data-testid="quote-save-submit-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva preventivo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
